/**
 * GroundLink Intranet — Platform Configuration
 * Defines all platforms, their access roles, visual identity, and routing.
 *
 * Card accents use ONLY the GLK brand palette (green/blue/yellow/orange/purple).
 * Crimson/emerald are reserved for status indicators, never card identity.
 * `colorEnd` (optional) turns a card's accents into a color→colorEnd gradient.
 */

export const ROLES = {
  SUPER_ADMIN:     'super_admin',
  ADMIN:           'admin',
  HR_MANAGER:      'hr_manager',
  HR_STAFF:        'hr_staff',
  SAFETY_OFFICER:  'safety_officer',
  TRAINING_MANAGER:'training_manager',
  TRAINER:         'trainer',
  MAINTENANCE_MANAGER:'maintenance_manager',
  MAINTENANCE_LEAD:'maintenance_lead',
  MAINTENANCE:     'maintenance',
  LOGISTICS_LEAD:  'logistics_lead',
  LOGISTICS:       'logistics',
  PLANNER:         'planner',
  SUPERVISOR:      'supervisor',
  STAFF:           'staff',
  // Safety station/ramp/FOH management roles + safety_manager
  // (2026-09-18-investigation-fallback-station-roles-design.md).
  SAFETY_MANAGER:          'safety_manager',
  STATION_MANAGER:         'station_manager',
  DEPUTY_STATION_MANAGER:  'deputy_station_manager',
  RAMP_MANAGER:            'ramp_manager',
  DEPUTY_RAMP_MANAGER:     'deputy_ramp_manager',
  FOH_MANAGER:             'foh_manager',
  RSO:                     'rso',
};

export const ROLE_HIERARCHY = [
  ROLES.SUPER_ADMIN,
  ROLES.ADMIN,
  ROLES.HR_MANAGER,
  ROLES.SAFETY_MANAGER,
  ROLES.SAFETY_OFFICER,
  ROLES.TRAINING_MANAGER,
  ROLES.MAINTENANCE_MANAGER,
  ROLES.MAINTENANCE_LEAD,
  ROLES.LOGISTICS_LEAD,
  ROLES.PLANNER,
  ROLES.STATION_MANAGER,
  ROLES.RAMP_MANAGER,
  ROLES.FOH_MANAGER,
  ROLES.RSO,
  ROLES.SUPERVISOR,
  ROLES.DEPUTY_STATION_MANAGER,
  ROLES.DEPUTY_RAMP_MANAGER,
  ROLES.TRAINER,
  ROLES.HR_STAFF,
  ROLES.MAINTENANCE,
  ROLES.LOGISTICS,
  ROLES.STAFF,
];

export const PLATFORMS = [
  {
    id:       'hr',
    title:    'Human Resources',
    subtitle: 'Staff & Personnel',
    description: 'Personnel records, contracts, payroll integration, shift management, and HR workflow automation.',
    path:     '/platforms/hr',
    color:    '#2D7D46',
    colorKey: 'teal',
    icon:     'users',
    bgAngle:  '135deg',
    status:   'online',
    badge:    'HR',
    roles:    [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.HR_MANAGER, ROLES.HR_STAFF, ROLES.SUPERVISOR, ROLES.STAFF],
    writeRoles: [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.HR_MANAGER, ROLES.HR_STAFF],
    accessLabel: 'All Staff',
    apiScope:  'hr:*',
    externalApp: '/hr',   // GLK HR module — separate deployed app, not an internal SPA route
  },
  {
    id:       'safety',
    title:    'Safety & Compliance',
    subtitle: 'SMS & Regulatory',
    description: 'Safety Management System, incident reports, IATA ISAGO audits, and regulatory compliance tracking.',
    path:     '/platforms/safety',
    color:    '#E8A000',
    colorKey: 'amber',
    icon:     'shield-check',
    bgAngle:  '120deg',
    status:   'online',
    badge:    'SMS',
    roles:    [
      ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.SAFETY_MANAGER, ROLES.SAFETY_OFFICER, ROLES.SUPERVISOR, ROLES.STAFF,
      ROLES.STATION_MANAGER, ROLES.DEPUTY_STATION_MANAGER, ROLES.RAMP_MANAGER, ROLES.DEPUTY_RAMP_MANAGER,
      ROLES.FOH_MANAGER, ROLES.RSO, ROLES.TRAINER,   // trainer: read-only
    ],
    writeRoles: [
      ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.SAFETY_MANAGER, ROLES.SAFETY_OFFICER, ROLES.SUPERVISOR,
      ROLES.STATION_MANAGER, ROLES.DEPUTY_STATION_MANAGER, ROLES.RAMP_MANAGER, ROLES.DEPUTY_RAMP_MANAGER,
      ROLES.FOH_MANAGER, ROLES.RSO,
    ],
    accessLabel: 'All Staff',
    apiScope:  'safety:*',
    externalApp: '/safety',
  },
  {
    id:       'training',
    title:    'Training Platform',
    subtitle: 'LMS & Certification',
    description: 'Learning management system, course delivery, certification tracking, and recurrent training schedules.',
    path:     '/platforms/training',
    color:    '#7C3AB8',
    colorKey: 'violet',
    icon:     'graduation-cap',
    bgAngle:  '145deg',
    status:   'online',
    badge:    'LMS',
    roles:    [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.TRAINING_MANAGER, ROLES.TRAINER, ROLES.SUPERVISOR, ROLES.STAFF],
    writeRoles: [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.TRAINING_MANAGER, ROLES.TRAINER],
    accessLabel: 'All Staff',
    apiScope:  'training:*',
    externalApp: '/training',   // Airport Compliance & Training Manager — separate deployed app, not an internal SPA route
  },
  {
    id:       'maintenance',
    title:    'Maintenance',
    subtitle: 'GSE & Facilities',
    description: 'Ground support equipment tracking, preventive maintenance work orders, fault reporting, and asset lifecycle.',
    path:     '/platforms/maintenance',
    color:    '#F5C800',
    colorKey: 'gold',
    icon:     'wrench',
    bgAngle:  '150deg',
    status:   'online',
    badge:    'GSE',
    roles:    [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.MAINTENANCE_MANAGER, ROLES.MAINTENANCE_LEAD, ROLES.MAINTENANCE, ROLES.SUPERVISOR],
    writeRoles: [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.MAINTENANCE_MANAGER, ROLES.MAINTENANCE_LEAD, ROLES.MAINTENANCE],
    accessLabel: 'Maintenance Team',
    apiScope:  'maintenance:*',
    externalApp: '/cmms',   // GLK CMMS module — separate deployed app, not an internal SPA route
  },
  {
    id:       'logistics',
    title:    'Logistics',
    subtitle: 'Operations & Fleet',
    description: 'Fleet management, cargo coordination, resource allocation, and ground handling operations tracking.',
    path:     '/platforms/logistics',
    color:    '#1A5E9E',
    colorKey: 'azure',
    icon:     'truck',
    bgAngle:  '125deg',
    status:   'online',
    badge:    'OPS',
    roles:    [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.LOGISTICS_LEAD, ROLES.LOGISTICS, ROLES.PLANNER, ROLES.SUPERVISOR],
    writeRoles: [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.LOGISTICS_LEAD, ROLES.LOGISTICS, ROLES.PLANNER],
    accessLabel: 'Operations',
    apiScope:  'logistics:*',
    externalApp: '/logistics',   // Logistics module — separate deployed app, not an internal SPA route
  },
  {
    id:       'dashboard',
    title:    'Dashboards & Planning',
    subtitle: 'Analytics & Scheduling',
    description: 'Real-time KPI dashboards, shift planning, staff scheduling, resource forecasting, and live operational view.',
    path:     '/platforms/dashboard',
    color:    '#2D7D46',
    colorEnd: '#1A5E9E',   // brand signature gradient (green → blue), flagship platform
    colorKey: 'teal',
    icon:     'bar-chart-3',
    bgAngle:  '130deg',
    status:   'online',
    badge:    'BI',
    roles:    [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.PLANNER, ROLES.SUPERVISOR, ROLES.HR_MANAGER, ROLES.LOGISTICS_LEAD],
    writeRoles: [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.PLANNER],
    accessLabel: 'Management',
    apiScope:  'dashboard:*',
    externalApp: 'https://staging.groundlinkhandling.pt',   // GroundOptimizer — separate app/domain, currently staging
    // This platform has shared staff DB access
    sharedStaffDB: true,
  },
];

// ─── Sub-platforms ────────────────────────────────────────────────────────────
// Every section opens into two sub-cards: a dashboard area (analytics/KPIs,
// management-scoped via writeRoles) and the platform itself (parent roles).
// Titles/descriptions are configurable here; structure is generated so new
// platforms get sub-cards for free.
const SUB_META = {
  hr: {
    dash:     ['HR Dashboard', 'Headcount, contracts, absence and certification KPIs.'],
    app:      ['HR Platform', 'Personnel records, contracts and HR workflows.'],
  },
  safety: {
    dash:     ['Safety Dashboard', 'Incident trends, audit status and compliance KPIs.'],
    app:      ['SMS Platform', 'Incident reporting, investigations and audits.'],
  },
  training: {
    dash:     ['Training Dashboard', 'Certification expiry, course completion and recurrency KPIs.'],
    app:      ['LMS Platform', 'Course delivery, exams and certification records.'],
  },
  maintenance: {
    dash:     ['Maintenance Dashboard', 'GSE availability, work-order backlog and fault KPIs.'],
    app:      ['CMMS Platform', 'Work orders, asset lifecycle and fault reporting.'],
  },
  logistics: {
    dash:     ['Logistics Dashboard', 'Fleet utilisation, cargo throughput and allocation KPIs.'],
    app:      ['OPS Platform', 'Fleet management, cargo coordination and allocation.'],
  },
  dashboard: {
    dash:     ['KPI Dashboard', 'Real-time operational overview across all platforms.'],
    app:      ['GroundOptimizer', 'Shift planning, rostering and staff scheduling.'],
  },
};

PLATFORMS.forEach((p) => {
  const meta = SUB_META[p.id];
  if (!meta || p.sub) return;
  p.sub = [
    {
      id:          `${p.id}-dashboard`,
      type:        'dashboard',
      title:       meta.dash[0],
      description: meta.dash[1],
      icon:        'bar-chart-3',
      path:        `${p.path}/dashboard`,
      externalApp: null,
      roles:       p.writeRoles,
    },
    {
      id:          `${p.id}-app`,
      type:        'app',
      title:       meta.app[0],
      description: meta.app[1],
      icon:        p.icon,
      path:        p.path,
      externalApp: p.externalApp,
      roles:       p.roles,
    },
  ];
});

/**
 * True when the user was granted a platform: an admin role, or the platform id in
 * the user's `platformAccess` list (the list admins edit in User Management, and the
 * one the backend and the SSO ticket check). The per-platform `roles` lists are not
 * consulted here: they would hide a platform an admin has explicitly granted.
 */
export function hasPlatform(user, platformId) {
  if (!user) return false;
  if (user.role === ROLES.SUPER_ADMIN || user.role === ROLES.ADMIN) return true;
  return Array.isArray(user.platformAccess) && user.platformAccess.includes(platformId);
}

/**
 * Returns the sub-platforms of a platform a user can open: the platform app for
 * anyone granted the platform, and the dashboard area only for those who are also
 * in the platform's writeRoles.
 */
export function getAccessibleSubPlatforms(platform, user) {
  if (!platform?.sub || !hasPlatform(user, platform.id)) return [];
  return platform.sub.filter((s) => s.type !== 'dashboard' || s.roles.includes(user.role));
}

/** Returns the platforms a user has been granted */
export function getAccessiblePlatforms(user) {
  return PLATFORMS.filter((p) => hasPlatform(user, p.id));
}

/** Returns true if the user can write to a platform */
export function canWrite(platform, userRole) {
  return platform.writeRoles.includes(userRole);
}

/** Find platform by id */
export function getPlatformById(id) {
  return PLATFORMS.find((p) => p.id === id);
}
